import { useCallback } from 'react';
import { toast } from 'sonner';
import { useUnifiedAuth } from '@/hooks/useUnifiedAuth';
import { useAppSelector } from '@/store/hooks';
import { fetchCollects } from '@/integrations/agreements/client';
import { buildCollectsFilter, mapCollectToTable, type CollectForTable } from '@/hooks/useCollectsApiData';
import type { FilterState } from '@/hooks/useFilters';
import { removeGeneratorPrefix } from '@/utils/establishmentUtils';
import { attachDocumentFlags, fetchDocumentFlags } from '@/services/collectDocumentFlags';
import { useGatewayMaterials } from '@/hooks/useGatewayMaterials';
import { buildMaterialUnitMap, unitForMaterial } from '@/utils/materialUnit';

const EXPORT_PAGE_SIZE = 1000;
const CONCURRENT_EXPORT_REQUESTS = 3;

const yieldToMainThread = async () => {
  await new Promise<void>((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
};

export const useRecurringCollectsCSVExport = (filters: FilterState) => {
  const { profile } = useUnifiedAuth();
  const generatorsFromRedux = useAppSelector((s) => s.establishmentAccess.generators);
  const establishmentsFromRedux = useAppSelector((s) => s.establishmentAccess.establishments);
  const { data: materials } = useGatewayMaterials();

  const handleExportCSV = useCallback(async () => {
    const toastId = toast.loading('Exportando relatório... 0%');

    try {
      const allData: CollectForTable[] = [];
      const seenCollectIds = new Set<string>();
      const buildPageFilter = (page: number) =>
        buildCollectsFilter(
          filters,
          profile,
          generatorsFromRedux,
          establishmentsFromRedux,
          page,
          EXPORT_PAGE_SIZE,
          { excludeStatus: ['planned'] }
        );

      const appendUniqueCollects = (collects: CollectForTable[]) => {
        const newItems = collects.filter((collect) => {
          if (seenCollectIds.has(collect.collect_id)) return false;
          seenCollectIds.add(collect.collect_id);
          return true;
        });
        allData.push(...newItems);
      };

      const firstPageResult = await fetchCollects(buildPageFilter(1));
      const firstPageMapped = (firstPageResult?.data ?? []).map(mapCollectToTable);
      appendUniqueCollects(firstPageMapped);

      const backendLastPage = firstPageResult?.pagination?.last_page ?? 1;
      const totalPages = Math.max(backendLastPage, 1);
      let processedPages = 1;

      toast.loading(
        `Exportando relatório... ${Math.round((processedPages / totalPages) * 100)}% (${processedPages}/${totalPages} páginas)`,
        { id: toastId },
      );

      const remainingPages: number[] = [];
      for (let page = 2; page <= totalPages; page += 1) {
        remainingPages.push(page);
      }

      for (let i = 0; i < remainingPages.length; i += CONCURRENT_EXPORT_REQUESTS) {
        const batchPages = remainingPages.slice(i, i + CONCURRENT_EXPORT_REQUESTS);
        const batchResults = await Promise.all(
          batchPages.map(async (page) => {
            const result = await fetchCollects(buildPageFilter(page));
            return { page, data: (result?.data ?? []).map(mapCollectToTable) };
          }),
        );

        batchResults
          .sort((a, b) => a.page - b.page)
          .forEach((result) => appendUniqueCollects(result.data));

        processedPages += batchPages.length;
        toast.loading(
          `Exportando relatório... ${Math.round((processedPages / totalPages) * 100)}% (${processedPages}/${totalPages} páginas)`,
          { id: toastId },
        );

        await yieldToMainThread();
      }

      if (allData.length === 0) {
        toast.warning('Nenhuma coleta encontrada para exportar', { id: toastId });
        return;
      }

      // Coluna MTR: situação por coleta no mtr-manager (lotes de 500); indisponível → vazio.
      const rows_ = attachDocumentFlags(allData, await fetchDocumentFlags(allData.map((c) => c.collect_id)));

      const escapeCsvValue = (value: unknown): string => {
        if (value == null) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const csvHeaders = [
        'ID da Coleta',
        'Estabelecimento',
        'Unidades',
        // `weight` já vem na unidade do material (kg, litros ou unidades): a planilha leva o número e
        // a unidade, sem o antigo "Volume" (weight × 1,1), que convertia o óleo duas vezes.
        'Quantidade coletada',
        'Unidade de medida',
        'Acondicionamento',
        'MTR',
        'Data',
        'Status de Coleta',
        'Tipo de Material',
        'Transportador',
      ];
      const materialUnits = buildMaterialUnitMap(materials);
      const csvRows = rows_.map((collect) => [
        escapeCsvValue(collect.collect_id),
        escapeCsvValue(removeGeneratorPrefix(collect.establishments?.establishment_name || collect.establishment_name || 'N/A')),
        escapeCsvValue(collect.units ?? 0),
        escapeCsvValue(Math.round(collect.weight ?? 0)),
        escapeCsvValue(unitForMaterial(collect.material_id, materialUnits)),
        escapeCsvValue(collect.packaging_name ?? 'N/A'),
        // `null` = o serviço não sabe (Fase 2); a planilha não pode afirmar "Não".
        escapeCsvValue(collect.has_mtr == null ? '' : collect.has_mtr ? 'Sim' : 'Não'),
        escapeCsvValue(collect.planned_date),
        escapeCsvValue(collect.sla_status ?? 'N/A'),
        escapeCsvValue(collect.kind_of_material ?? 'N/A'),
        escapeCsvValue(collect.hauler_name ?? 'N/A'),
      ]);

      const csvContent = [csvHeaders.join(','), ...csvRows.map((row) => row.join(','))].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `coletas_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(`Relatório exportado com sucesso! (${allData.length} coletas)`, { id: toastId });
    } catch (error) {
      console.error('Erro ao exportar CSV:', error);
      toast.error('Erro ao exportar relatório', { id: toastId });
    }
  }, [filters, profile, generatorsFromRedux, establishmentsFromRedux, materials]);

  return { handleExportCSV };
};
